import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import Switch from "~/components/formElements/Switch";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateQuotaStatus } from "~/modules/client/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";
import QuotaStatusBadge from "~/modules/client/components/quota/QuotaDetailsView/components/QuotaStatusBadge.tsx";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	isBookable: boolean;
}

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations
};

const UpdateQuotaStatusForm = ({
	onCancel,
	onSuccess,
	quota,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			isBookable: yup.boolean().required(),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			isBookable: quota.isBookable,
		};
	}, [quota.isBookable]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			await updateQuotaStatus(quota.clientId, quota.id, data.isBookable);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [onSuccess, quota.clientId, quota.id]);


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Status des Abrechnungskontingents bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Aktueller Status">
						<QuotaStatusBadge isBookable={quota.isBookable} />
					</FormSection>
					<FormSection title="Neuer Status">
						<Switch labelOn="Aktiv"
								control={control}
								name="isBookable"
								labelOff="Inaktiv"
								showAllLabels={true} />
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable || !isDirty}>
					{t("form:buttonTexts.update")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateQuotaStatusForm;
